import { useAuth, useUser } from '@clerk/clerk-react'
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router'
import * as Sentry from '@sentry/react'

import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useStampLearnerTimeZone } from '@/hooks/useStampLearnerTimeZone'
import { useTicketHandoffPending } from '@/lib/ticketHandoff'
import { getAccountStatus } from '@/utils/getAccountStatus'
import { AccountStatusPage } from './AccountStatusPage'

type Props = {
  readonly children: React.ReactNode
}

function AuthLoadingState() {
  return (
    <div className='flex min-h-screen items-center justify-center bg-linear-to-br from-slate-50 to-slate-100'>
      <div className='space-y-4 text-center'>
        <div className='mx-auto h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900' />
        <p className='text-sm text-slate-500'>Verifying your account...</p>
      </div>
    </div>
  )
}

// Guard to check if the signed-in user exists in the database and has active status
export function DatabaseUserGuard({ children }: Props) {
  const { isSignedIn } = useAuth()
  const { user: clerkUser } = useUser()
  const { user, isLoading, error, errorStatus, isNetworkFailure, isRetryPending, refetch } = useCurrentUser()
  const isTicketHandoff = useTicketHandoffPending()
  const { pathname } = useLocation()
  const userEmail = clerkUser?.primaryEmailAddress?.emailAddress
  useStampLearnerTimeZone(user)

  useEffect(() => {
    if (user) Sentry.setUser({ id: user.id })
  }, [user])

  useEffect(() => {
    if (!isSignedIn || isLoading) return
    // 403 is a known auth-state condition (cross-org email collision); the
    // AuthError page captures its own deduped event, so don't double-report.
    if (errorStatus === 403) return
    // A ticket sign-in is about to replace this document, so an in-flight
    // request was aborted by the unload rather than failing for real.
    if (isTicketHandoff) return
    // The request never reached the server and SWR is still retrying. Most of
    // these recover on the next attempt, so reporting here counted one warning
    // per transient blip rather than per learner actually locked out.
    if (isRetryPending) return
    // A cached user means the session is already working, so a failed
    // revalidation behind it changed nothing the learner can see.
    if (user) return
    if (error) {
      // The error string stays out of the message: it's the browser's wording
      // for the same condition ("Load failed" on WebKit, "Failed to fetch" on
      // Chrome), so interpolating it split one defect across Sentry issues.
      Sentry.captureMessage('DatabaseUserGuard: /users/me failed', {
        level: 'warning',
        extra: { error, errorStatus },
      })
    } else {
      Sentry.captureMessage('DatabaseUserGuard: /users/me returned no user', {
        level: 'warning',
      })
    }
  }, [isSignedIn, isLoading, error, errorStatus, user, isTicketHandoff, isRetryPending])

  if (!isSignedIn) {
    return <>{children}</>
  }

  if (isLoading) {
    return <AuthLoadingState />
  }

  // Cross-org email collision: backend returns 403, frontend has a dedicated
  // wrong_organization page that explains the situation. Without this branch
  // the user would see the generic "user not found" page and reload repeatedly.
  if (errorStatus === 403) {
    // The error page renders inside this guard; redirecting to it again renders nothing.
    if (pathname === '/auth/error') return <>{children}</>
    const params = new URLSearchParams({ reason: 'wrong_organization', source: 'clerk' })
    if (userEmail) params.set('email', userEmail)
    return <Navigate to={`/auth/error?${params.toString()}`} replace />
  }

  // Only the absence of a user blocks the app. SWR keeps serving the cached user
  // alongside an `error`, so gating on `error` too would let a failed background
  // revalidation (the 60s trial AC poll, any remount past the deduping interval)
  // unmount a working session mid-lesson over one dropped packet.
  if (!user) {
    // Two reasons to hold the verifying state rather than call it a failure:
    // the unload race above, where a pending navigation is about to take over;
    // and a dropped request SWR is still retrying, where flashing a network
    // error at someone who lands in the app a second later is just noise.
    if (isTicketHandoff || isRetryPending) {
      return <AuthLoadingState />
    }

    return (
      <AccountStatusPage
        status={isNetworkFailure ? 'network_error' : 'not_found'}
        onRetry={isNetworkFailure ? refetch : undefined}
        userEmail={userEmail}
      />
    )
  }

  // Check if user's organization is deactivated
  if (user.organization_deactivated_at) {
    return <AccountStatusPage status='organization_deactivated' userEmail={userEmail} />
  }

  // Check if user is inactive or deactivated
  const accountStatus = getAccountStatus(user.status)
  if (accountStatus) {
    return <AccountStatusPage status={accountStatus} userEmail={userEmail} />
  }

  return <>{children}</>
}
